import { DPIA_DECISION_OPTIONS } from 'config/api/dpias/dpias.constants';
import type { DpiaDecision } from 'config/api/dpias/dpias.types';

import { DPIA_CRITERIA_VERSION, DPIA_TRIGGER_SOURCE_OPTIONS } from './dpiaRequests.constants';
import { calculateDpiaRecommendation } from './dpiaRequests.helpers';
import type {
  CancelDpiaRequestInput,
  DecideDpiaRequestInput,
  DpiaHistoryList,
  DpiaHistoryListItem,
  DpiaRecommendation,
  DpiaRequest,
  DpiaRequestContext,
  DpiaRequestDecision,
  DpiaRequestHistoryItemKind,
  DpiaRequestOpenResponse,
  DpiaRequestStatus,
  DpiaRequestTriggerSource,
  DpiaScreeningCriteriaCatalogue,
  DpiaScreeningCriterionCode,
  OpenDpiaRequestInput,
  ReassessDpiaInput,
  SaveDpiaRequestDraftInput,
} from './dpiaRequests.types';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null && !Array.isArray(value);

const parseDpiaHistoryDecision = (value: unknown): DpiaDecision | null =>
  typeof value === 'string' && (DPIA_DECISION_OPTIONS as readonly string[]).includes(value) ? (value as DpiaDecision) : null;

const ENVELOPE_PARSE_ERROR = 'Expected { request, context } from dpia-requests open/GET';

const RECOMMENDATION_FROM_BE: Record<string, DpiaRecommendation> = {
  normally_not_recommended: 'normally_not_recommended',
  further_assessment_recommended: 'further_assessment_recommended',
  dpia_recommended: 'dpia_recommended',
  dpia_strongly_recommended: 'dpia_strongly_recommended',
  dpia_not_recommended: 'normally_not_recommended',
  dpia_may_be_recommended: 'further_assessment_recommended',
};

const asString = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);

const asBoolean = (value: unknown, fallback = false): boolean => (typeof value === 'boolean' ? value : fallback);

const asNullableNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

const asNumber = (value: unknown, fallback = 0): number => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

const asNullableString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const parseSelectedCriteria = (dto: UnknownRecord): DpiaScreeningCriterionCode[] => {
  const raw = dto.selected_criteria ?? dto.criteria_codes;
  let codes: string[] = [];

  if (Array.isArray(raw)) {
    codes = raw.filter((item): item is string => typeof item === 'string');
  } else if (isRecord(raw)) {
    if (Array.isArray(raw.codes)) {
      codes = raw.codes.filter((item): item is string => typeof item === 'string');
    } else if (Array.isArray(raw.selected)) {
      codes = raw.selected.filter((item): item is string => typeof item === 'string');
    } else {
      codes = Object.entries(raw)
        .filter(([, enabled]) => enabled === true || enabled === 'true' || enabled === 1)
        .map(([code]) => code);
    }
  }

  return codes;
};

const parseTriggerSource = (value: unknown): DpiaRequestTriggerSource => {
  if (typeof value === 'string' && (DPIA_TRIGGER_SOURCE_OPTIONS as readonly string[]).includes(value)) {
    return value as DpiaRequestTriggerSource;
  }

  return 'ropa_administrator';
};

const parseRecommendation = (value: unknown, selectedCriteria: DpiaScreeningCriterionCode[], alwaysRule: boolean): DpiaRecommendation => {
  if (typeof value === 'string' && RECOMMENDATION_FROM_BE[value]) {
    return RECOMMENDATION_FROM_BE[value];
  }

  return calculateDpiaRecommendation(selectedCriteria, alwaysRule);
};

const parseDecisionFromStatus = (status: DpiaRequestStatus, explicitDecision: unknown): DpiaRequestDecision | null => {
  if (explicitDecision === 'dpia_required' || explicitDecision === 'dpia_not_required') {
    return explicitDecision;
  }

  if (status === 'decided_dpia_required') {
    return 'dpia_required';
  }

  if (status === 'decided_dpia_not_required') {
    return 'dpia_not_required';
  }

  return null;
};

const parseStatus = (value: unknown): DpiaRequestStatus => {
  if (
    value === 'indicated' ||
    value === 'draft' ||
    value === 'decided_dpia_required' ||
    value === 'decided_dpia_not_required' ||
    value === 'cancelled'
  ) {
    return value;
  }

  return 'draft';
};

const parseHistoryKind = (value: unknown): DpiaRequestHistoryItemKind => {
  if (value === 'dpia') {
    return 'dpia';
  }

  return 'dpia_request';
};

const parseCategories = (value: unknown) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord).map(item => ({
    id: asNumber(item.id),
    name: asString(item.name),
  }));
};

const parsePurposes = (value: unknown) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord).map(item => ({
    id: asNumber(item.id),
    purposeText: asString(item.purpose_text),
    applicabilityStatus: asString(item.applicability_status),
  }));
};

const parseCountrySummary = (value: unknown): DpiaRequestContext['countrySummary'] => {
  const dto = isRecord(value) ? value : {};
  const countryCodes = Array.isArray(dto.country_codes) ? dto.country_codes.filter((item): item is string => typeof item === 'string') : [];

  return {
    count: asNumber(dto.count, countryCodes.length),
    countryCodes,
    hasThirdCountryProcessing: asBoolean(dto.has_third_country_processing),
  };
};

const parseIndicators = (value: unknown): DpiaRequestContext['indicators'] => {
  const dto = isRecord(value) ? value : {};

  return {
    thirdCountryAccess: asBoolean(dto.third_country_access),
    dpiaIndicated: asBoolean(dto.dpia_indicated),
    minimumSecurityPartiallyMet: asBoolean(dto.minimum_security_partially_met),
    sensitivePersonalDataFlagged: asBoolean(dto.sensitive_personal_data_flagged),
  };
};

const parseHistory = (value: unknown): DpiaRequestContext['history'] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord).map(item => ({
    id: asNumber(item.id),
    kind: parseHistoryKind(item.kind),
    status: asString(item.status),
    createdAt: asString(item.created_at),
    assessedAt: asNullableString(item.assessed_at),
    title: asNullableString(item.title),
    approvedAt: asNullableString(item.approved_at),
    indicatedAt: asNullableString(item.indicated_at),
    indicationComment: asNullableString(item.indication_comment),
  }));
};

const unwrapRequestRecord = (payload: unknown): UnknownRecord => {
  if (!isRecord(payload)) {
    return {};
  }

  if (isRecord(payload.request)) {
    return payload.request;
  }

  return payload;
};

export const parseDpiaRequestForFE = (dtoInput: unknown): DpiaRequest => {
  const dto = unwrapRequestRecord(dtoInput);
  const selectedCriteria = parseSelectedCriteria(dto);
  const alwaysDpiaRuleTriggered = asBoolean(dto.always_dpia_rule_triggered);
  const status = parseStatus(dto.status);

  return {
    id: asNumber(dto.id),
    assetProcessingActivityId: asNumber(dto.asset_processing_activity_id),
    status,
    criteriaVersion: asString(dto.criteria_version, DPIA_CRITERIA_VERSION),
    triggerSource: parseTriggerSource(dto.trigger_source),
    triggerComment: asString(dto.trigger_comment),
    selectedCriteria,
    criteriaAssessmentComment: asString(dto.criteria_assessment_comment ?? dto.criteria_comment),
    alwaysDpiaRuleTriggered,
    alwaysDpiaRuleComment: asString(dto.always_dpia_rule_comment),
    recommendation: parseRecommendation(dto.recommendation, selectedCriteria, alwaysDpiaRuleTriggered),
    decision: parseDecisionFromStatus(status, dto.decision),
    decisionComment: asString(dto.decision_comment),
    decisionDeviatesFromRecommendation: asBoolean(dto.decision_deviates_from_recommendation),
    cancellationComment: asNullableString(dto.cancellation_comment),
    createdByMembershipId: asNullableNumber(dto.created_by_membership_id),
    assessedByMembershipId: asNullableNumber(dto.assessed_by_membership_id),
    assessedAt: asNullableString(dto.assessed_at),
    cancelledByMembershipId: asNullableNumber(dto.cancelled_by_membership_id),
    cancelledAt: asNullableString(dto.cancelled_at),
    createdDpiaId: asNullableNumber(dto.created_dpia_id),
    sourceRopaReviewRoundId: asNullableNumber(dto.source_ropa_review_round_id),
    sourceAssetProcessingActivityReviewId: asNullableNumber(dto.source_asset_processing_activity_review_id),
    updatedAt: asNullableString(dto.updated_at),
  };
};

export const parseDpiaRequestContextForFE = (dto: unknown): DpiaRequestContext => {
  if (!isRecord(dto)) {
    throw new Error(ENVELOPE_PARSE_ERROR);
  }

  return {
    assetId: asNumber(dto.asset_id),
    assetName: asString(dto.asset_name),
    processingActivityId: asNumber(dto.processing_activity_id),
    processingActivityName: asString(dto.processing_activity_name ?? dto.asset_processing_activity_name),
    processingActivityStatus: asNullableString(dto.processing_activity_status),
    assetOwnerId: asNumber(dto.asset_owner_id),
    assetOwnerName: asString(dto.asset_owner_name),
    dataSubjectCategories: parseCategories(dto.data_subject_categories),
    personalDataCategories: parseCategories(dto.personal_data_categories),
    activePurposesCount: asNumber(dto.active_purposes_count),
    activePurposes: parsePurposes(dto.active_purposes),
    countrySummary: parseCountrySummary(dto.country_summary),
    minimumSecurityAssessment: asString(dto.minimum_security_assessment),
    dpiaIndicationStatus: asString(dto.dpia_indication_status),
    indicators: parseIndicators(dto.indicators),
    previousRequestsCount: asNumber(dto.previous_requests_count),
    previousDpiasCount: asNumber(dto.previous_dpias_count),
    history: parseHistory(dto.history),
  };
};

export const parseDpiaRequestOpenResponseForFE = (dto: unknown): DpiaRequestOpenResponse => {
  if (!isRecord(dto) || !isRecord(dto.request) || !isRecord(dto.context)) {
    throw new Error(ENVELOPE_PARSE_ERROR);
  }

  const request = parseDpiaRequestForFE(dto.request);
  if (!request.id) {
    throw new Error('DPIA need assessment response is missing request id');
  }

  return {
    request,
    context: parseDpiaRequestContextForFE(dto.context),
  };
};

export const parseDpiaHistoryForFE = (dto: unknown): DpiaHistoryList => {
  const payload = isRecord(dto) ? dto : {};
  const itemsRaw = Array.isArray(payload.items) ? payload.items : [];

  return {
    assetProcessingActivityId: asNumber(payload.asset_processing_activity_id),
    items: itemsRaw
      .filter(isRecord)
      .filter(item => item.kind === 'dpia' || item.kind === 'dpia_request' || item.kind === 'dpia_indication')
      .map(item => ({
        id: asNumber(item.id),
        kind: item.kind as DpiaHistoryListItem['kind'],
        status: asString(item.status),
        isCurrent: asBoolean(item.is_current),
        title: asNullableString(item.title),
        createdAt: asString(item.created_at),
        approvedAt: asNullableString(item.approved_at),
        decision: parseDpiaHistoryDecision(item.decision),
        supersedesDpiaId: asNullableNumber(item.supersedes_dpia_id),
      })),
  };
};

export const parseScreeningCriteriaCatalogueForFE = (dto: unknown): DpiaScreeningCriteriaCatalogue => {
  const payload = isRecord(dto) ? dto : {};
  const criteriaRaw = Array.isArray(payload.criteria) ? payload.criteria : [];

  return {
    version: asString(payload.version, DPIA_CRITERIA_VERSION),
    criteria: criteriaRaw
      .filter(isRecord)
      .map(item => ({
        code: asString(item.code),
        sortOrder: asNumber(item.sort_order),
        labelEn: asString(item.label_en),
        labelSv: asString(item.label_sv),
      }))
      .sort((left, right) => left.sortOrder - right.sortOrder),
  };
};

export const parseOpenDpiaRequestForBE = (payload: OpenDpiaRequestInput = {}) => ({
  ...(payload.sourceRopaReviewRoundId !== undefined ? { source_ropa_review_round_id: payload.sourceRopaReviewRoundId } : {}),
  ...(payload.sourceAssetProcessingActivityReviewId !== undefined
    ? { source_asset_processing_activity_review_id: payload.sourceAssetProcessingActivityReviewId }
    : {}),
});

const parseDraftPayloadForBE = (payload: SaveDpiaRequestDraftInput) => ({
  trigger_source: payload.triggerSource,
  trigger_comment: payload.triggerComment.trim(),
  criteria_codes: payload.selectedCriteria,
  criteria_comment: payload.criteriaAssessmentComment.trim(),
  always_dpia_rule_triggered: payload.alwaysDpiaRuleTriggered,
  always_dpia_rule_comment: payload.alwaysDpiaRuleTriggered ? payload.alwaysDpiaRuleComment.trim() : '',
});

export const parseSaveDpiaRequestDraftForBE = (payload: SaveDpiaRequestDraftInput) => ({
  ...parseDraftPayloadForBE(payload),
  decision: payload.decision,
});

export const parseDecideDpiaRequestForBE = (payload: DecideDpiaRequestInput) => ({
  ...parseDraftPayloadForBE(payload),
  decision_comment: payload.decisionComment.trim(),
});

export const parseCancelDpiaRequestForBE = (payload: CancelDpiaRequestInput) => ({
  cancellation_comment: payload.cancellationComment.trim(),
});

export const parseReassessDpiaForBE = (payload: ReassessDpiaInput) => ({
  reason: payload.reason.trim(),
});
